import StopoverSidePanel from "app/pages/Stopover/StopoverSidePanel/StopoverSidePanel";
import isEmpty from "lodash/isEmpty";
import { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import {
	fetchStopoverFlightOptions,
	fetchStopoverPackage,
} from "app/pages/Stopover/stopoverActionCreators";
import { bindActionCreators } from "redux";
import { HTTP_STATUS_CODES, QUOTATION_CODE_STATUS } from "app/constants";
import { resetSDPSearchQueryString } from "app/pages/SmartDP/smartDPActionCreators";
import get from "lodash/get";
import { mapProductToTag } from "app/utils/analytics-store-mapper";
import { useLocation, useNavigate } from "react-router-dom";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { parse } from "qs";
import { useSelector, shallowEqual, useDispatch } from "react-redux";

const StopoverSidePanelContainer = ownProps => {
	const stateProps = useSelector(mapStateToProps, shallowEqual);
	const dispatch = useDispatch();
	const dispatchProps = useMemo(() => mapDispatchToProps(dispatch), [dispatch]);
	const props = { ...ownProps, ...stateProps, ...dispatchProps };

	const {
		bookingFlowId,
		handleCloseSelectedflightRecap = {},
		fetchStopoverFlightOptions = () => {},
		fetchStopoverPackage = () => {},
		resetSDPSearchQueryString = () => {},
		shop,
	} = props;
	const navigate = useNavigate();
	const { track } = useAnalytics();

	const { search } = useLocation();
	const queryParams = parse(search, { ignoreQueryPrefix: true });

	const handleDurationChange = useCallback(
		duration => {
			fetchStopoverPackage({ bookingFlowId, stopOverDuration: duration }).then(response => {
				const httpStatusCode = response.status;
				const data = response.data || {};
				if (httpStatusCode === HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR) {
					handleCloseSelectedflightRecap();
					navigate(
						{
							pathname: "/technical-error",
						},
						{
							replace: true,
							state: {
								errorMessage: data.message,
							},
						}
					);
					track("stopover_fetch_package", {
						httpStatusCode,
						queryParams: queryParams,
					});
				} else if (httpStatusCode === HTTP_STATUS_CODES.BAD_REQUEST) {
					const status = data.status;

					track("stopover_fetch_products", {
						httpStatusCode,
						status,
						queryParams: queryParams,
					});
				} else if (httpStatusCode === HTTP_STATUS_CODES.FORBIDDEN) {
					// reset the stored query string so after authentication the quote can be relaunch
					resetSDPSearchQueryString();

					handleCloseSelectedflightRecap();

					const onauthsuccess = encodeURIComponent(
						`${location.pathname.replace(`/${shop}`, "")}${location.search}`
					);

					navigate(
						{
							pathname: "/booking-auth",
							search: `?onauthsuccess=${onauthsuccess}`,
						},
						{ replace: true }
					);
				} else {
					const status = data.status;
					const productCount = data.packages?.accommodationsList.length;
					const specialOffer = get(data, "specialOffer");

					const products = data.packages?.accommodationsList || [];

					const mappedProducts = products
						.map(mapProductToTag)
						.map((product, position) => ({ ...product, position }));

					track("product_list", {
						list: "PRODUCT_LIST",
						productCount,
						queryParams: queryParams,
						products: mappedProducts.slice(0, 12),
					});

					track("stopover_fetch_products", {
						httpStatusCode,
						status,
						productCount,
						queryParams: queryParams,
						specialOffer,
					});

					const includedFlight = data.packages?.flights.find(flight => flight.included);

					if (includedFlight?.code) {
						fetchStopoverFlightOptions({
							flight: includedFlight?.code,
							bookingFlowId: response?.data.bookingFlowId,
							accommodationId: queryParams.destinationResortCode,
							stopOverDuration: Number(duration),
						}).then(response => {
							const flightOptionsHttpStatus = response.status;

							if (
								!flightOptionsHttpStatus ||
								flightOptionsHttpStatus ===
									HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR ||
								flightOptionsHttpStatus === HTTP_STATUS_CODES.GATEWAY_TIMEOUT ||
								flightOptionsHttpStatus === HTTP_STATUS_CODES.BAD_REQUEST ||
								flightOptionsHttpStatus === HTTP_STATUS_CODES.FORBIDDEN
							) {
								track("flightOptions_fail");
							}
						});
					}
				}
			});
		},
		[bookingFlowId]
	);

	return <StopoverSidePanel {...props} onDurationChange={handleDurationChange} />;
};

StopoverSidePanelContainer.propTypes = {
	initializeForm: PropTypes.func,
	bookingFlowId: PropTypes.string,
	formInitialValues: PropTypes.object,
	shop: PropTypes.string,
	handleCloseSelectedflightRecap: PropTypes.func,
	resetSDPSearchQueryString: PropTypes.func,
	fetchStopoverPackage: PropTypes.func,
	fetchStopoverFlightOptions: PropTypes.func,
};

const mapStateToProps = state => {
	const booking = state.booking;

	const formInitialValues = {
		flight: !isEmpty(booking.flight) ? booking.flight.code : undefined,
		transfer: !isEmpty(booking.transfer) ? booking.transfer.code : undefined,
		luggage: booking.luggage,
		duration: booking.stopoverDuration,
	};

	return {
		formInitialValues,
		shop: state.shop,
		bookingFlowId: state.quotation.code,
		isLoading: state.quotation.status === QUOTATION_CODE_STATUS.LOADING,
	};
};

const mapDispatchToProps = dispatch => {
	return bindActionCreators(
		{
			fetchStopoverPackage,
			fetchStopoverFlightOptions,
			resetSDPSearchQueryString,
		},
		dispatch
	);
};

export default StopoverSidePanelContainer;
